/**
 * THE COMMITTEE IS IN SESSION, said from wherever you are.
 *
 * `design_handoff_rules_ia` §3, "Announcement elsewhere": reuse the `DraftStrip` idiom exactly —
 * a dark strip above the topnav, a green LIVE pill on the League nav item, and on a phone the
 * strip goes to the BOTTOM, above the tab bar, per the note in `DraftStrip.tsx` about thumb
 * reach. Same idiom, one evening a year, for the same reason: a manager who has left the page
 * needs to know a vote is open and have one way back.
 *
 * IT SAYS WHETHER YOU HAVE VOTED, which is the whole difference between this and a banner. "Item
 * 2 of 6 is open" is a fact about the meeting; "you have not voted on it" is a fact about you,
 * and it is the one that decides whether the strip is worth interrupting anybody for. Once you
 * have voted the strip stays, quieter, because the room is still running and the next item is
 * still coming.
 *
 * THE CLOCK IS ELAPSED, NOT REMAINING — the same argument `domain/draftStrip.ts` makes. There is
 * no per-item allowance in this league; what is knowable is how long the item has been open, and
 * that answers the question the number is there for.
 */

import type { Vote } from './proposals.js';

export interface CommitteeInfo {
  /** False for all but one evening a year, and the only thing a caller should branch on. */
  live: boolean;
  /** 1-based docket position of the open item. */
  itemNo: number | null;
  total: number;
  title: string | null;
  /** Has this manager's vote been recorded on the open item. */
  voted: boolean;
  /** When the item opened, for the elapsed clock. Null when unmeasurable. */
  since: string | null;
}

const OFF: CommitteeInfo = {
  live: false, itemNo: null, total: 0, title: null, voted: false, since: null,
};

export interface CommitteeInput {
  /** Null outside the meeting phase — the caller derives that from the docket. */
  openItemId?: string;
  openedAt?: string;
  committedAt?: string;
  order?: readonly string[];
  agenda: ReadonlyArray<{ id: string; title: string; recordedAt?: string;
    votes?: Record<string, Vote> }>;
  me: string;
}

export function committeeStrip(input: CommitteeInput): CommitteeInfo {
  if (input.committedAt || !input.openItemId || !input.me) return OFF;
  const order = input.order ?? input.agenda.map((p) => p.id);
  const at = order.indexOf(input.openItemId);
  if (at < 0) return OFF;
  const item = input.agenda.find((p) => p.id === input.openItemId);
  if (!item) return OFF;
  /*
   * A RECORDED ITEM IS NOT A LIVE ONE even while it is still the open one. The commissioner has
   * written the outcome; nothing is being asked of anybody, and a strip saying "go and vote"
   * would be pointing at three disabled buttons.
   */
  if (item.recordedAt) return OFF;

  return {
    live: true,
    itemNo: at + 1,
    total: order.length,
    title: item.title,
    voted: Boolean(item.votes?.[input.me.toUpperCase()]),
    since: input.openedAt ?? null,
  };
}

/** What the strip says, in one clause. The phone gets the short one. */
export function committeeSaid(info: CommitteeInfo, narrow = false): string {
  if (!info.live) return '';
  const where = `Item ${info.itemNo} of ${info.total}`;
  if (narrow) return info.voted ? `${where} · voted` : `${where} · you have not voted`;
  return info.voted
    ? `${where} is open — your vote is in`
    : `${where} is open and you have not voted`;
}
